import type { Currency } from 'src/types/listing';
import type { ChainKey, ChainFamily } from 'src/blockchain';
import type { ApiChainToken, ApiListingChain, ApiAvailableChain } from 'src/types/chain';

import { ENABLED_FAMILIES } from 'src/blockchain';

/** A network the buyer can actually be offered, with everything the payment needs. */
export interface PurchaseChainOption {
  chainKey: ChainKey;
  family: ChainFamily;
  displayName: string;
  /** EVM chain id to switch the wallet to before the transfer; absent on non-EVM chains. */
  chainId?: number;
  /** Token that settles *this* listing here — address and decimals build the transfer. */
  token: ApiChainToken;
  /** Where the money must land: the seller's wallet in this chain's family. */
  sellerPayoutAddress: string;
  /**
   * What to transfer, as quoted by api — the listing price, with no conversion (see
   * `ApiListingChain.amount`). Absent against an api that predates the field.
   */
  amount?: number;
  /** Explorer URL prefix a transaction hash is appended to. */
  explorerTxUrl: string;
}

interface ResolveParams {
  /** `GET /buyer/chains` — platform-enabled ∩ caller region. */
  buyerChains: ApiAvailableChain[] | undefined;
  /** The listing's `availableChains` — carries this listing's currency ∩ seller has a wallet. */
  listingChains: ApiListingChain[] | undefined;
  /** Fiat currency of the listing; decides which stablecoin settles the purchase. */
  listingCurrency: Currency;
  /**
   * Families the buyer's account has linked. `undefined` means api did not say (a deployment that
   * predates the field) — the factor is then skipped rather than treated as "none linked", which
   * would leave nothing payable at all. An empty array is an answer: no wallet linked, nothing to
   * pay with.
   */
  linkedFamilies: ChainFamily[] | undefined;
  /**
   * Families whose wallet stack this build actually carries. Defaults to the build's own set;
   * a parameter so the intersection can be tested without rebuilding with another env.
   */
  buildFamilies?: ChainFamily[];
}

/**
 * The networks a Buy Now purchase may be offered on (multichain spec §4.3).
 *
 * The intersection has four factors and each one is owned by whoever can be trusted with it:
 *
 * 1. **enabled by the platform ∩ allowed in the caller's region ∩ open to new purchases** —
 *    `GET /buyer/chains`. The region gate is computed server-side from the request IP; the client
 *    never decides it. A chain listed with `purchases: false` (TRON since api#401) carries the
 *    wallets and purchases that already live on it and takes no new one.
 * 2. **carries the stablecoin of the listing's currency ∩ the seller has a wallet in the family** —
 *    the listing's `availableChains`, computed by the same server service that gate-keeps the
 *    create call, so the selector can never offer a chain the purchase is then refused on.
 * 3. **the buyer has linked a wallet of that family** — checked here, from `/user/auth/me`.
 *    Without it the buyer would pay the seller and only then be told the purchase cannot be
 *    recorded: the transfer happens client-side and is irreversible. Skipped when api does not
 *    report the wallets at all (see `linkedFamilies`) — an unknown must not read as a refusal.
 * 4. **this build carries the family's wallet stack** — a reduced `VITE_ENABLED_CHAINS` build has
 *    no EVM wallet to connect with, so offering an EVM chain would dead-end at the connect step.
 *
 * Everything else the payment needs (token address, decimals, explorer prefix, EVM chain id) is
 * read from the api registry entry, never hardcoded here.
 */
export function resolvePurchaseChainOptions(params: ResolveParams): PurchaseChainOption[] {
  const { buyerChains, listingChains, listingCurrency, linkedFamilies, buildFamilies = ENABLED_FAMILIES } = params;

  if (!buyerChains?.length || !listingChains?.length) {
    return [];
  }

  const byChainKey = new Map(buyerChains.map((chain) => [chain.chainKey, chain]));

  return listingChains.flatMap((listingChain): PurchaseChainOption[] => {
    const chain = byChainKey.get(listingChain.chainKey);
    const token = chain?.tokens[listingCurrency];

    if (
      !chain ||
      chain.purchases === false ||
      !token ||
      (linkedFamilies !== undefined && !linkedFamilies.includes(listingChain.family)) ||
      !buildFamilies.includes(listingChain.family)
    ) {
      return [];
    }

    return [
      {
        chainKey: chain.chainKey,
        family: listingChain.family,
        displayName: chain.displayName,
        chainId: chain.chainId,
        token,
        sellerPayoutAddress: listingChain.sellerPayoutAddress,
        amount: listingChain.amount,
        explorerTxUrl: chain.explorerTxUrl,
      },
    ];
  });
}

/** Explorer link for a transaction on a chosen network — the prefix comes from api, not from us. */
export const explorerTxLink = (option: PurchaseChainOption, txHash: string): string =>
  `${option.explorerTxUrl}${txHash}`;

/**
 * The listing's chain list, with a fallback for an api that predates the field.
 *
 * The mirror of what MPR-3 did in the other direction: it made `chainKey` optional so an app
 * deployed before it kept working. This is the same courtesy for the deploy window where this
 * build is live and api is not yet — without it Buy Now would go dark for everyone in between,
 * since `undefined` chains means no network to offer.
 *
 * The fallback is the pre-multichain behaviour stated exactly: the seller is paid on their own
 * listing wallet, on the one enabled chain of that wallet's family. It applies only when the field
 * is **absent** — an empty array is api saying "nowhere", which is an answer, not a gap. And it
 * refuses to guess when the family spans several enabled chains, which an api old enough to lack
 * the field never had.
 */
export function listingChainsOrLegacy(params: {
  listingChains: ApiListingChain[] | undefined;
  buyerChains: ApiAvailableChain[] | undefined;
  sellerAddress: string | undefined;
  sellerFamily: ChainFamily | undefined;
}): ApiListingChain[] | undefined {
  const { listingChains, buyerChains, sellerAddress, sellerFamily } = params;

  if (listingChains !== undefined) {
    return listingChains;
  }

  if (!sellerAddress || !sellerFamily) {
    return undefined;
  }

  const ofFamily = (buyerChains ?? []).filter((chain) => chain.family === sellerFamily);

  if (ofFamily.length !== 1) {
    return undefined;
  }

  return [
    {
      chainKey: ofFamily[0].chainKey,
      family: sellerFamily,
      // Only ever read for display; the token that settles the purchase is looked up from the
      // registry entry by the listing's currency, exactly as on the non-legacy path.
      tokenSymbol: '',
      // `amount` is deliberately left out: an api this old quotes no amount, and the modal then
      // pays `listing.price` — the same number, by the peg rule.
      sellerPayoutAddress: sellerAddress,
    },
  ];
}
